import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, uploadImage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PhotoSection } from "@/pages/jobs/CheckIn";
import { UkDatePicker } from "@/components/UkDatePicker";
import { todayIso, nowHM } from "@/lib/dateFormat";
import { toast } from "sonner";

export default function Handover() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(params.get("job_id") || "");
  const [mileageOut, setMileageOut] = useState("");
  const [condition, setCondition] = useState("");
  const [keysReturned, setKeysReturned] = useState(true);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collectionException, setCollectionException] = useState("");
  const [handDate, setHandDate] = useState(todayIso());
  const [handTime, setHandTime] = useState(nowHM());

  useEffect(() => { api("/jobs?status=completed").then(setJobs).catch(() => {}); }, []);

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const up = await uploadImage(f, { context: "handover" });
        setPhotos((p) => [...p, { id: up.id, url: up.url }]);
      }
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!jobId) { toast.error("Select a job"); return; }
    setSaving(true);
    try {
      await api(`/workshop/jobs/${jobId}/handover`, { method: "POST", body: JSON.stringify({ mileage_out: mileageOut ? Number(mileageOut) : null, final_condition: condition, keys_returned: keysReturned, notes, photos: photos.map((p) => p.id), collection_exception: collectionException.trim() || null, handed_at: `${handDate}T${handTime || "00:00"}:00` }) });
      setCollectionException("");
      toast.success("Handover recorded");
      navigate(`/jobs/${jobId}`);
    } catch (e2) {
      if (e2.body?.detail?.error === "outstanding_balance") {
        toast.error(`Outstanding balance of £${Number(e2.body.detail.balance || 0).toFixed(2)}. Enter an authorised collection exception reason to proceed.`);
      } else { toast.error(e2.message); }
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-xl">
      <Link to="/jobs" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Jobs</Link>
      <PageHeader title="Vehicle Handover" subtitle="Record collection & final condition" />
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-card p-6">
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">JOB *</span>
          <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="handover-job" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand">
            <option value="">Select a job…</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.vehicle?.vrm} — {j.title}</option>)}</select></label>
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">MILEAGE OUT</span>
          <input type="number" value={mileageOut} onChange={(e) => setMileageOut(e.target.value)} data-testid="handover-mileage" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">HANDOVER DATE</span>
            <UkDatePicker value={handDate} onChange={setHandDate} testId="handover-date" /></div>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">TIME</span>
            <input type="time" value={handTime} onChange={(e) => setHandTime(e.target.value)} data-testid="handover-time" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        </div>
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">FINAL CONDITION</span>
          <textarea value={condition} onChange={(e) => setCondition(e.target.value)} data-testid="handover-condition" rows={2} className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        <PhotoSection photos={photos} onPhoto={onPhoto} uploading={uploading} onRemove={(id) => setPhotos((p) => p.filter((x) => x.id !== id))} testid="handover" />
        <label className="flex items-center gap-3"><input type="checkbox" checked={keysReturned} onChange={(e) => setKeysReturned(e.target.checked)} data-testid="handover-keys" className="h-5 w-5 rounded border-input" /><span className="text-sm font-semibold text-midnight">Keys returned to customer</span></label>
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">NOTES</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="handover-notes" rows={2} className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-amber-700">Collection exception (only if balance is outstanding)</span>
          <textarea value={collectionException} onChange={(e) => setCollectionException(e.target.value)} data-testid="handover-collection-exception" rows={2} placeholder="Authorised reason to release the vehicle before full payment" className="w-full rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3 text-sm outline-none focus:border-amber-500" /></label>
        <button type="submit" disabled={saving} data-testid="handover-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "COMPLETE HANDOVER"}
        </button>
      </form>
    </div>
  );
}
